import styled from "@emotion/styled";
import { InputAdornment, Stack } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  ChartLineDown,
  ChartLineUp,
  MagnifyingGlass
} from "@phosphor-icons/react";
import { Button, TextField } from "components/UI";
import { useChurchStore } from "features/churches";
import { useExportTransactions, useTransactions } from "features/finances";
import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "react-use";

const Container = styled.div`
  grid-area: transactions;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
`;

const Title = styled.h6`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  margin-right: 80px;
`;

const Navigation = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-right: auto;
`;

const NavigationItem = styled.span`
  font-size: 12px;
  color: ${({ active, theme }) =>
    active ? theme.palette.primary.main : "#64748b"};
  font-weight: ${({ active }) => (active ? 600 : 400)};
  cursor: pointer;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SearchIcon = styled(MagnifyingGlass)`
  color: ${({ theme }) => theme.palette.primary.main};
`;

export const TransactionsTable = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [type, setType] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutate: exportTransactions, isLoading: isExporting } =
    useExportTransactions();

  const { data: transactions, isLoading } = useTransactions({
    churchId: selectedChurch?.id,
    page,
    pageSize,
    type,
    queries: {
      search: debouncedSearch
    }
  });

  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    500,
    [search]
  );

  const columns = [
    {
      field: "transaction_number",
      headerName: "Transaction Number",
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Stack direction="row" alignItems="center" spacing={2}>
            {row.entry_type === "credit" ? (
              <ChartLineUp color="#74DFD5" size={24} />
            ) : (
              <ChartLineDown color="#F8BC77" size={24} />
            )}
            <span>{row.transaction_number || ""}</span>
          </Stack>
        );
      }
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      valueGetter: ({ row }) => {
        return row.chart_of_account?.account_name || "";
      }
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      valueGetter: ({ row }) => {
        return row.description || "";
      }
    },
    {
      field: "debits",
      headerName: "Debits",
      flex: 1,
      valueGetter: ({ row }) => {
        return row.entry_type === "debit" ? row.amount?.toLocaleString() : "";
      }
    },
    {
      field: "credits",
      headerName: "Credits",
      flex: 1,
      valueGetter: ({ row }) => {
        return row.entry_type === "credit" ? row.amount?.toLocaleString() : "";
      }
    },
    {
      field: "balance",
      headerName: "Balance",
      flex: 1,
      valueGetter: ({ row }) => {
        return row.balance?.toLocaleString() || "";
      }
    },
    {
      field: "date",
      headerName: "Transaction date",
      flex: 1,
      valueGetter: ({ row }) => {
        return moment(row.entry_date).format("DD-MM-YYYY");
      }
    }
  ];

  return (
    <Container>
      <Header>
        <Title>Transactions</Title>
        <Navigation>
          <NavigationItem
            active={type === "all"}
            onClick={() => setType("all")}
          >
            All
          </NavigationItem>
          <NavigationItem
            active={type === "revenue"}
            onClick={() => setType("revenue")}
          >
            Revenue
          </NavigationItem>
          <NavigationItem
            active={type === "expense"}
            onClick={() => setType("expense")}
          >
            Expense
          </NavigationItem>
          <NavigationItem
            active={type === "void"}
            onClick={() => setType("void")}
          >
            Void
          </NavigationItem>
        </Navigation>
        <Actions>
          <TextField
            neutral={1}
            placeholder="Search transactions"
            register={() => {}}
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={16} />
                </InputAdornment>
              )
            }}
            autoComplete="off"
            onChange={e => setSearch(e.target.value)}
          />
          <Button
            onClick={() =>
              exportTransactions({
                churchId: selectedChurch?.id,
                queries: {
                  search: debouncedSearch,
                  type
                }
              })
            }
            disabled={isExporting}
          >
            Export
          </Button>
        </Actions>
      </Header>
      <DataGrid
        autoHeight
        rows={transactions?.data || []}
        rowCount={transactions?.total || 0}
        loading={isLoading}
        columns={columns}
        pagination
        rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
        density="comfortable"
        getRowId={row => row.id}
        disableColumnMenu
        disableSelectionOnClick
        paginationMode="server"
        page={page - 1}
        pageSize={pageSize}
        onPageChange={newPage => setPage(newPage + 1)}
        onPageSizeChange={newPageSize => setPageSize(newPageSize)}
        localeText={{
          noRowsLabel: t("teams.teamProfile.NoRows"),
          MuiTablePagination: {
            labelRowsPerPage: t("teams.teamProfile.RowsPerPage")
          }
        }}
        onRowClick={({ row }) => {
          navigate(
            `/finances/temp/chart-of-accounts/${row.chart_of_account?.id}?accountName=${row.chart_of_account?.account_name}&accountNumber=${row.chart_of_account?.account_number}&accountType=${row.chart_of_account?.account_type}`
          );
        }}
      />
    </Container>
  );
};
